import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import { createProvisionalConversationAgent } from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import {
	type Conversation,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { persistRecentConversation } from '../recent-conversation-persistence/recentConversationPersistence';

import { conversationMetadataForStore } from './conversationMetadataForStore';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { initializeConversationData } from './initializeConversationData';
import { setConversationData } from './setConversationData';
import type { ChatStoreAction } from './types';

type SetSelectedConversationArgs = {
	conversationId: string;
	sourceAgentId?: string;
	/**
	 * When the id is not yet in `conversations` (e.g. opened from space-scoped GET list),
	 * seed the store from this payload so selection is not downgraded to empty chat.
	 */
	conversationMetadata?: Conversation & { isEmpty?: unknown; currentTask?: unknown };
	/**
	 * When set, ChatHistory will scroll to this specific message after the conversation loads.
	 * Used by Go to Chat from the Tasks List to route to the task's completion/current message.
	 */
	targetMessageId?: string | null;
	/** Preserve the legacy expiry cleanup when hydration intentionally resets an expired target. */
	clearLegacyLastMessageSentAt?: boolean;
};

export const setSelectedConversation: SafeAction<[SetSelectedConversationArgs]> = createSafeAction(
	StoreActionLoggableName.setSelectedConversation,
	({
		conversationId,
		sourceAgentId,
		conversationMetadata,
		targetMessageId,
		clearLegacyLastMessageSentAt,
	}: SetSelectedConversationArgs): ChatStoreAction =>
		({ setState, getState, dispatch }, { siteId }) => {
			let resolvedConversationId = conversationId;

			if (!getState().conversations[conversationId]) {
				if (conversationMetadata?.id === conversationId) {
					dispatch(
						initializeConversationData({
							conversationId,
							data: {
								...conversationMetadataForStore(conversationMetadata),
								...(targetMessageId ? { targetTaskMessageId: targetMessageId } : {}),
							},
						}),
					);
				} else {
					resolvedConversationId = EMPTY_CONVERSATION_ID;
				}
			} else if (targetMessageId) {
				dispatch(
					setConversationData({
						conversationId,
						data: { targetTaskMessageId: targetMessageId },
					}),
				);
			}

			setState({
				currentConversationId: resolvedConversationId,
			});

			// Keep the agent id provided when selecting this conversation; gate-on also seeds `conversation.agent` for the header.
			if (sourceAgentId) {
				const conversationAgentFromSource = isExperimentEnabled('rovo_chat_mention_agents')
					? createProvisionalConversationAgent(sourceAgentId)
					: undefined;
				const conversationAgentSourceData = conversationAgentFromSource
					? { agent: conversationAgentFromSource }
					: {};
				dispatch(
					setConversationData({
						conversationId: resolvedConversationId,
						data: {
							sourceAgentId,
							...conversationAgentSourceData,
						},
					}),
				);
			}

			// Store preference.
			// For space-scoped conversations, store EMPTY_CONVERSATION_ID instead of the
			// client-generated UUID. The client UUID is never returned by the backend /conversations
			// API, so restoring it on refresh creates an orphaned optimistic entry that has no
			// rovoSpaceId and cannot be matched to any API channel — causing a ghost item to
			// appear in the regular conversation list. Starting fresh (empty chat) on refresh
			// is the correct behaviour for space conversations.
			const conversationForStorage = getState().conversations[resolvedConversationId];
			const localStorageConversationId =
				conversationForStorage?.rovoSpaceId &&
				FeatureGates.getExperimentValue('rovo_chat_spaces_experiment', 'isEnabled', false)
					? EMPTY_CONVERSATION_ID
					: resolvedConversationId;
			persistRecentConversation({
				type: 'conversation-selected',
				siteId,
				conversationId: localStorageConversationId,
				clearLegacyLastMessageSentAt,
			});
		},
);
